<script>
import lazyLoadComponent from '@router/lazyload-component'
import { orderStatusFilter, orderSourceFilter, orderPaymentMethodFilter } from '@utils/filter-value-to-text'
import formatDate from '@utils/format-date'
import pageMixin from '@src/mixins/pagination'

export default {
  components: {
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    PrepaidOrderDetail: () => import('@components/Orders/PrepaidOrderDetail'),
  },
  filters: {
    orderStatusFilter,
    orderSourceFilter,
    orderPaymentMethodFilter,
    formatDate,
  },
  mixins: [pageMixin],
  data() {
    return {
      loadedOrders: [],
      prepaidOrders: [],
      tryingToGetOrder: false,
      drawerVisible: false,
      drawerOrderData: '',
      filterData: {
        order_by: 'created_at',
        order: 'desc',
      },
      componentKey: 0,
      appStyle: process.env.VUE_APP_BUILD_MODE === 'admin-app-build',
    }
  },

  watch: {
    currentPage: {
      handler: 'tryToSearchOrder',
      immediate: true,
    },
  },
  methods: {
    calcRowClassName({ row }) {
      const orderStatus = row.status
      const orderUsedStatus = row.groupSize / row.usedSize

      if (orderStatus === 0) return 'order--canceled'
      if (orderUsedStatus === 1) return 'order--used'
    },
    async tryToGetOrder(row) {
      this.tryingToGetOrder = true
      this.drawerOrderData = await this.$store.dispatch('prepaid/fetchOrderSingle', row.id)
      this.tryingToGetOrder = false
      this.drawerVisible = true
    },
    async tryToSearchOrder() {
      const { items, total } = await this.$store.dispatch(
        'prepaid/fetchOrders',
        Object.assign(
          {
            memberId: this.$route.params.memberId,
            paymentMethod: 'All',
            order_by: this.filterData.order_by,
            order: this.filterData.order,
          },
          this.pageQuery
        )
      )
      this.loadedOrders = items
      this.$nextTick(() => {
        this.$refs.pagination && this.$refs.pagination.updateTotal(total)
      })
    },
    sortChange({ column, prop, order }) {
      if (order) {
        this.filterData.order_by = prop
        this.filterData.order = order === 'ascending' ? 'asc' : 'desc'
        this.currentPage = 1
        this.tryToSearchOrder()
      }
      // console.log(column, prop, order)
    },
    async getPrepaidInfo() {
      this.tryingToGetOrder = true
      this.prepaidOrders = []
      for (const item of this.loadedOrders) {
        const data = await this.$store.dispatch('prepaid/fetchOrderSingle', item.id)
        this.prepaidOrders.push(data)
      }
      this.tryingToGetOrder = false
    },
    prepaidUsedTimes(data) {
      return `${data.name}: ${data.maxTimes - data.used} / ${data.maxTimes}`
    },
    expiredDataComputed(row) {
      const { expiredDate, extendedExpiredDate } = row

      if (extendedExpiredDate) {
        return formatDate(extendedExpiredDate)
      }
      if (expiredDate) {
        return formatDate(expiredDate)
      }
      return '無使用期限'
    },
  },
}
</script>

<template>
  <div>
    <BasePageHeader :title="`${$t('prepaid.BASE')}方案`"> </BasePageHeader>
    <div class="bg-white">
      <div class="el-table-wrapper">
        <el-table
          v-loading.fullscreen.lock="tryingToGetOrder"
          element-loading-text="取得訂單資訊中"
          :data="loadedOrders"
          :default-sort="{ prop: 'date', order: 'descending' }"
          :row-class-name="calcRowClassName"
          class="isBgDark"
          @row-click="tryToGetOrder"
          @sort-change="sortChange"
        >
          <el-table-column v-slot="{ row }" label="訂單編號" prop="created_at" min-width="160" sortable="custom">
            <div>{{ `#${row.orderNo}` }}</div>
            <div class="text-secondary-light fs-sm Jost">{{ row.created | formatDate('dateTime') }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" prop="status" label="訂單狀態" min-width="110" sortable="custom">
            <BaseStateLabel :state="row.status | orderStatusFilter('color')" text>{{ row.status | orderStatusFilter('text') }}</BaseStateLabel>
            <br />
            <BaseStateLabel class="text-secondary-light fs-sm" text>{{ row.paymentMethod | orderPaymentMethodFilter }}</BaseStateLabel>
          </el-table-column>

          <el-table-column v-slot="{ row }" min-width="170" :label="`${$t('prepaid.BASE')}名稱`">
            <div>
              <b>{{ row.prepaidTitle }}</b>
            </div>
            <div class="text-secondary-light fs-sm">{{ row.sizeTitle }}</div>
            <div class="text-secondary-light fs-sm Jost">${{ row.total }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" min-width="120" label="次數與期限">
            <div>
              <b>可抵用次數：{{ row.times - row.used }} / {{ row.times }}</b>
              <span v-for="prepaid in row.prices" :key="prepaid.id" style="display: block;">{{ prepaidUsedTimes(prepaid) }}</span>
            </div>
            <div class="text-secondary-light fs-sm Jost">{{ row.storeTitles.join(' | ') }}</div>
            <div v-if="row.status === 1" class="text-secondary-light fs-sm Jost">到期：{{ expiredDataComputed(row) }}</div>
          </el-table-column>
          <el-table-column min-width="40">
            <el-tooltip content="查看訂單詳情" placement="top">
              <el-button type="text">
                <BaseIcon name="ellipsis-v" />
              </el-button>
            </el-tooltip>
          </el-table-column>
        </el-table>
      </div>
      <BasePagination ref="pagination" :current-page.sync="currentPage" />
    </div>
    <el-drawer
      ref="drawer"
      class="only-close"
      :class="{ appStyle: appStyle }"
      custom-class="order-drawer"
      append-to-body
      :modal-append-to-body="false"
      :wrapper-closable="false"
      :visible.sync="drawerVisible"
      :size="$root.isSmallDevice ? '100%' : '90%'"
      :destroy-on-close="true"
      @closed="componentKey++"
    >
      <PrepaidOrderDetail ref="orderDetail" order-type="prepaid" :default-value.sync="drawerOrderData" @update="tryToSearchOrder" />
    </el-drawer>
  </div>
</template>
